// The structural half of a styled city centre: foundation (team plinth + base
// course), the wall shell and the roof builders (including the pagoda, which
// owns its own walls). Roof and wall rules are lookup tables keyed by the
// style's `roof` shape; nothing here branches on a building kind.

import {
  type Ctx,
  type Roofed,
  TEAM_FOLD,
  TEAM_LIT,
  type V,
  WALL_Y,
  box,
  shell,
  slab,
} from './buildings_styled_kit.js';
import type { RoofShape } from './styles.js';

// --- Foundation: team plinth border + base course --------------------------

export function foundation(c: Ctx): V[] {
  const { B } = c.spec;
  return [
    ...box(-B, 0, -B, 2 * B + 1, 1, 2 * B + 1, TEAM_LIT, true),
    // Fold line so the plinth reads as paving, not a flat slab.
    ...box(-B, 0, -B, 2 * B + 1, 1, 1, TEAM_FOLD, true),
    ...box(-B, 0, B, 2 * B + 1, 1, 1, TEAM_FOLD, true),
    ...slab(B - 1, 1, c.p.base),
  ];
}

// --- Walls ------------------------------------------------------------------

type WallRule = (c: Ctx) => (x: number, y: number, z: number) => string;

/** Wall colouring per roof family: plaster + cornice, plank stripes, plaster + vermilion posts. */
const WALL_RULES: Readonly<Record<RoofShape, WallRule>> = {
  'hip-low': (c) => (_x, y) => (y === WALL_Y + c.spec.wallH - 1 ? c.p.trim : c.p.wall),
  'gable-steep': (c) => (x, _y, z) => ((x + z) % 2 === 0 ? c.p.wall : c.p.wallAlt),
  'swept-tiers': (c) => (x, y, z) =>
    (Math.abs(x) === c.spec.wallHalf && Math.abs(z) === c.spec.wallHalf) ||
    y === WALL_Y + c.spec.wallH - 1
      ? c.p.trim
      : c.p.wall,
  shared: (c) => () => c.p.wall,
};

export function walls(
  c: Ctx,
  roof: RoofShape,
  half = c.spec.wallHalf,
  y0 = WALL_Y,
  h = c.spec.wallH,
): V[] {
  const out = shell(half, y0, h, WALL_RULES[roof](c));
  if (y0 !== WALL_Y) return out;
  // Door and windows on the front (+z) face.
  out.push(
    { x: 0, y: y0, z: half, color: c.p.door },
    { x: 0, y: y0 + 1, z: half, color: c.p.door },
  );
  for (const wx of half >= 3 ? [-2, 2] : [-1, 1]) {
    out.push({ x: wx, y: y0 + 1, z: half, color: c.p.glass });
  }
  return out;
}

// --- Roofs: return voxels plus the apex (x=0,z=0) y of the topmost layer ----

type RoofBuilder = (c: Ctx) => Roofed;

export const ROOFS: Readonly<Record<RoofShape, RoofBuilder>> = {
  // Low hip: three shrinking slabs, terracotta with a lighter ridge course.
  'hip-low': (c) => {
    const y0 = WALL_Y + c.spec.wallH;
    const blocks: V[] = [...shell(c.spec.B, y0, 1, () => c.p.trim)];
    let y = y0;
    for (let ext = c.spec.B; ext >= c.spec.B - 2; ext--) {
      blocks.push(...slab(ext, y, y === y0 + 2 ? c.p.roofAlt : c.p.roof));
      y++;
    }
    return { blocks, topY: y - 1 };
  },
  // Steep gable: ridge along x, one layer narrower per step, plank gable ends, crossed finials.
  'gable-steep': (c) => {
    const y0 = WALL_Y + c.spec.wallH;
    const xe = c.spec.wallHalf + 1;
    const blocks: V[] = [];
    let y = y0;
    for (let zh = c.spec.wallHalf + 1; zh >= 0; zh--, y++) {
      for (let x = -xe; x <= xe; x++)
        for (let z = -zh; z <= zh; z++)
          blocks.push({
            x,
            y,
            z,
            color: Math.abs(x) === xe ? c.p.wallAlt : (z + y) % 2 === 0 ? c.p.roof : c.p.roofAlt,
          });
    }
    const ridge = y - 1;
    for (const sx of [-xe, xe]) {
      blocks.push(
        { x: sx, y: ridge + 1, z: 0, color: c.p.accent },
        { x: sx, y: ridge + 2, z: 0, color: c.p.accent },
        { x: sx, y: ridge + 1, z: -1, color: c.p.accent },
        { x: sx, y: ridge + 1, z: 1, color: c.p.accent },
      );
    }
    return { blocks, topY: ridge };
  },
  // Pagoda tiers: built by `pagoda` below, which owns walls and roofs together.
  'swept-tiers': (c) => pagoda(c),
  shared: (c) => ({
    blocks: slab(c.spec.B, WALL_Y + c.spec.wallH, c.p.roof),
    topY: WALL_Y + c.spec.wallH,
  }),
};

/** Eastern: stacked tiers of (walls, swept eave, step), corner tips curling up. */
function pagoda(c: Ctx): Roofed {
  const { tier } = c;
  const tiers: ReadonlyArray<{ wall: number; eave: number; wallH: number }> =
    tier === 1
      ? [{ wall: 2, eave: 3, wallH: 3 }]
      : tier === 2
        ? [
            { wall: 3, eave: 4, wallH: 2 },
            { wall: 1, eave: 2, wallH: 2 },
          ]
        : [
            { wall: 4, eave: 5, wallH: 2 },
            { wall: 2, eave: 3, wallH: 1 },
            { wall: 1, eave: 2, wallH: 1 },
          ];
  const blocks: V[] = [];
  let y = WALL_Y;
  for (const t of tiers) {
    // Plaster walls with vermilion corner posts and a beam band on the top layer.
    blocks.push(
      ...shell(t.wall, y, t.wallH, (x, _y, z) =>
        Math.abs(x) === t.wall && Math.abs(z) === t.wall ? c.p.trim : c.p.wall,
      ),
      ...shell(t.wall, y + t.wallH - 1, 1, () => c.p.trim),
    );
    if (y === WALL_Y) {
      blocks.push(
        { x: 0, y, z: t.wall, color: c.p.door },
        { x: 0, y: y + 1, z: t.wall, color: c.p.door },
      );
    }
    y += t.wallH;
    // Eave slab with a vermilion edge board, so the swept silhouette reads from above.
    blocks.push(...slab(t.eave, y, c.p.roof), ...shell(t.eave, y, 1, () => c.p.trim));
    // Upswept corners: the eave tips curl above the roof line.
    for (const sx of [-t.eave, t.eave])
      for (const sz of [-t.eave, t.eave])
        blocks.push({ x: sx, y: y + 1, z: sz, color: c.p.roofAlt });
    y++;
    blocks.push(...slab(Math.max(0, t.eave - 1), y, c.p.roofAlt));
    y++;
  }
  blocks.push({ x: 0, y, z: 0, color: c.p.accent });
  return { blocks, topY: y };
}
